// Helper KYC: document upload tracking + reviewer workflow.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const DOC_TYPES = ["id_front", "id_back", "selfie", "address_proof", "right_to_work", "dbs", "insurance", "trade_certificate"] as const;
type DocType = (typeof DOC_TYPES)[number];

export const REQUIRED_KYC_DOC_TYPES: ReadonlyArray<DocType> = ["id_front", "selfie", "address_proof", "right_to_work"];
export const EXPIRY_REQUIRED_DOC_TYPES: ReadonlyArray<DocType> = ["right_to_work", "dbs", "insurance", "trade_certificate"];

// Allowed MIME types + magic-byte signatures for inline AV/content scan.
const MIME_ALLOWLIST = new Set(["image/jpeg", "image/png", "image/webp", "image/heic", "application/pdf"]);
const MAX_BYTES = 10 * 1024 * 1024;

function sniffMagic(bytes: Uint8Array): string | null {
  if (bytes.length < 4) return null;
  // JPEG FF D8 FF
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  // PNG 89 50 4E 47
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return "image/png";
  // WEBP "RIFF....WEBP"
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
      bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return "image/webp";
  // HEIC: bytes 4..7 'ftyp' + brand heic/heix/heim/heis/mif1
  if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) return "image/heic";
  // PDF "%PDF"
  if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) return "application/pdf";
  return null;
}

// Content scan: pulls the first 32KB from storage and verifies the magic bytes
// match the declared MIME, and that obvious bad-actor payloads (eg. MZ/exe,
// shell shebangs, HTML/JS) are rejected. This is the antivirus boundary.
async function scanStoredDoc(storagePath: string): Promise<{ status: "clean" | "quarantined" | "error"; sniffed: string | null; reason?: string }> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin.storage.from("helper-kyc").download(storagePath);
    if (error || !data) return { status: "error", sniffed: null, reason: error?.message };
    const buf = new Uint8Array(await data.slice(0, 32 * 1024).arrayBuffer());
    if (buf.length === 0) return { status: "quarantined", sniffed: null, reason: "empty_file" };

    // Block executables / scripts / archives outright
    // MZ (Windows EXE), ELF, Mach-O, shebangs, PK ZIP, RAR
    if (buf[0] === 0x4d && buf[1] === 0x5a) return { status: "quarantined", sniffed: null, reason: "executable" };
    if (buf[0] === 0x7f && buf[1] === 0x45 && buf[2] === 0x4c && buf[3] === 0x46) return { status: "quarantined", sniffed: null, reason: "executable" };
    if (buf[0] === 0x23 && buf[1] === 0x21) return { status: "quarantined", sniffed: null, reason: "script" };
    if (buf[0] === 0x50 && buf[1] === 0x4b) return { status: "quarantined", sniffed: null, reason: "archive" };

    const text = new TextDecoder("utf-8", { fatal: false }).decode(buf).slice(0, 2048).toLowerCase();
    if (text.includes("<script") || text.includes("<?php") || text.startsWith("<!doctype html") || text.startsWith("<html")) {
      return { status: "quarantined", sniffed: null, reason: "html_script" };
    }
    // EICAR antivirus test signature
    if (text.includes("x5o!p%@ap[4\\pzx54(p^)7cc)7}$eicar")) return { status: "quarantined", sniffed: null, reason: "eicar_test_signature" };

    const sniffed = sniffMagic(buf);
    if (!sniffed) return { status: "quarantined", sniffed: null, reason: "unknown_magic" };
    return { status: "clean", sniffed };
  } catch (err) {
    return { status: "error", sniffed: null, reason: err instanceof Error ? err.message : "scan_failed" };
  }
}

// ----- Record a document the client just uploaded to storage -----
export const recordHelperDoc = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      docType: z.enum(DOC_TYPES),
      storagePath: z.string().min(3).max(512),
      originalFilename: z.string().max(256).optional(),
      mimeType: z.string().max(128).optional(),
      sizeBytes: z.number().int().positive().max(MAX_BYTES).optional(),
      expiresAt: z.string().datetime().optional(),
      issuedAt: z.string().datetime().optional(),
      referenceNumber: z.string().trim().max(64).optional(),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    if (!data.storagePath.startsWith(`${userId}/`)) throw new Error("invalid_path");
    if (data.mimeType && !MIME_ALLOWLIST.has(data.mimeType)) throw new Error("unsupported_file_type");
    if (EXPIRY_REQUIRED_DOC_TYPES.includes(data.docType) && !data.expiresAt) throw new Error("expiry_required");

    // Per-user rate limit (8 uploads/hour overall)
    const { data: ok } = await supabase.rpc("check_kyc_upload_rate", { _helper_id: userId });
    if (ok === false) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin.from("helper_verification_audit").insert({
          helper_id: userId,
          event_type: "resubmission_blocked",
          actor_id: userId,
          actor_role: "helper",
          notes: "Global hourly upload limit reached (8/hour)",
          metadata: { doc_type: data.docType, scope: "global_hourly" },
        } as never);
      } catch { /* ignore */ }
      throw new Error("rate_limited: please wait before uploading more documents");
    }

    // Per-doc-type cooldown: 60 seconds between consecutive uploads of the same type,
    // and at most 5 resubmissions per doc type per 24 hours.
    const COOLDOWN_SECONDS = 60;
    const DAILY_PER_TYPE = 5;
    const { data: recent } = await supabase
      .from("helper_documents")
      .select("created_at")
      .eq("helper_id", userId)
      .eq("doc_type", data.docType)
      .gte("created_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
      .order("created_at", { ascending: false });
    const recentRows = (recent ?? []) as Array<{ created_at: string }>;
    const lastAt = recentRows[0]?.created_at ? new Date(recentRows[0].created_at).getTime() : 0;
    const secondsSince = lastAt ? Math.floor((Date.now() - lastAt) / 1000) : Infinity;
    const dailyCount = recentRows.length;

    if (secondsSince < COOLDOWN_SECONDS || dailyCount >= DAILY_PER_TYPE) {
      const reason = secondsSince < COOLDOWN_SECONDS
        ? `cooldown_active: please wait ${COOLDOWN_SECONDS - secondsSince}s before re-uploading ${data.docType}`
        : `daily_limit_reached: max ${DAILY_PER_TYPE} uploads per 24h for ${data.docType}`;
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin.from("helper_verification_audit").insert({
          helper_id: userId,
          event_type: "resubmission_blocked",
          actor_id: userId,
          actor_role: "helper",
          notes: reason,
          metadata: {
            doc_type: data.docType,
            scope: secondsSince < COOLDOWN_SECONDS ? "cooldown" : "daily_per_type",
            seconds_since_last: Number.isFinite(secondsSince) ? secondsSince : null,
            daily_count: dailyCount,
            cooldown_seconds: COOLDOWN_SECONDS,
            daily_limit: DAILY_PER_TYPE,
          },
        } as never);
      } catch { /* ignore */ }
      throw new Error(reason);
    }

    // Antivirus / content scan
    const scan = await scanStoredDoc(data.storagePath);
    if (scan.status === "quarantined") {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin.storage.from("helper-kyc").remove([data.storagePath]);
        await supabaseAdmin.from("helper_verification_audit").insert({
          helper_id: userId,
          event_type: "document_quarantined",
          actor_id: userId,
          actor_role: "system",
          notes: `Scan rejected ${data.docType}: ${scan.reason ?? "failed safety scan"}`,
          metadata: {
            doc_type: data.docType,
            scan: {
              provider: "peopels-inline-av",
              status: "quarantined",
              verdict: "malicious_or_invalid",
              reason: scan.reason ?? null,
              scanned_at: new Date().toISOString(),
            },
          },
        } as never);
      } catch { /* ignore */ }
      throw new Error(`file_rejected: ${scan.reason ?? "failed safety scan"}`);
    }

    // Versioned resubmission: mark prior active docs of the same type as superseded
    // (preserve history instead of deleting).
    const { data: hp } = await supabase
      .from("helper_profiles")
      .select("verification_attempt_number")
      .eq("id", userId)
      .maybeSingle();
    const attemptNumber = (hp as { verification_attempt_number?: number } | null)?.verification_attempt_number ?? 1;

    const { data: priorDocs } = await supabase
      .from("helper_documents")
      .select("id")
      .eq("helper_id", userId)
      .eq("doc_type", data.docType)
      .in("status", ["submitted", "approved", "rejected"]);
    const priorIds = (priorDocs ?? []).map((d) => d.id as string);

    const { data: inserted, error } = await supabase.from("helper_documents").insert({
      helper_id: userId,
      doc_type: data.docType,
      storage_path: data.storagePath,
      original_filename: data.originalFilename ?? null,
      mime_type: scan.sniffed ?? data.mimeType ?? null,
      size_bytes: data.sizeBytes ?? null,
      status: "submitted",
      expires_at: data.expiresAt ?? null,
      issued_at: data.issuedAt ?? null,
      reference_number: data.referenceNumber ?? null,
      scan_status: scan.status,
      scan_result: scan as never,
      attempt_number: attemptNumber,
    } as never).select("id").single();
    if (error || !inserted) throw new Error(error?.message ?? "insert_failed");

    if (priorIds.length) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.from("helper_documents")
        .update({ status: "superseded", superseded_at: new Date().toISOString(), superseded_by: (inserted as { id: string }).id } as never)
        .in("id", priorIds);
    }

    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.from("helper_verification_audit").insert({
        helper_id: userId,
        attempt_number: attemptNumber,
        event_type: priorIds.length ? "document_resubmitted" : "document_uploaded",
        actor_id: userId,
        actor_role: "helper",
        notes: `${data.docType} uploaded`,
        metadata: {
          doc_type: data.docType,
          document_id: (inserted as { id: string }).id,
          superseded_document_ids: priorIds,
          mime_type: scan.sniffed ?? data.mimeType ?? null,
          size_bytes: data.sizeBytes ?? null,
          scan: {
            provider: "peopels-inline-av",
            status: scan.status,
            verdict: scan.status === "clean" ? "clean" : scan.status === "error" ? "scan_error" : "malicious_or_invalid",
            reason: (scan as { reason?: string }).reason ?? null,
            sniffed_mime: scan.sniffed,
            scanned_at: new Date().toISOString(),
          },
        },
      } as never);
    } catch (err) { console.error("[kyc.record] audit failed", err); }

    return { ok: true, scan_status: scan.status, document_id: (inserted as { id: string }).id, attempt_number: attemptNumber };
  });

// ----- List my KYC docs + current verification status -----
export const getMyKycStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: docs } = await supabase
      .from("helper_documents")
      .select("id, doc_type, status, original_filename, mime_type, size_bytes, reviewer_notes, reviewed_at, created_at, expires_at, issued_at, reference_number, scan_status, decision_code")
      .eq("helper_id", userId)
      .order("created_at", { ascending: false });

    const { data: prof } = await supabase
      .from("helper_profiles")
      .select("verification_status, kyc_submitted_at, kyc_reviewed_at, kyc_reviewer_notes, right_to_work_verified, right_to_work_expires_at, dbs_verified, dbs_expires_at")
      .eq("id", userId)
      .maybeSingle();

    const { data: prefs } = await supabase
      .from("notification_preferences")
      .select("kyc_reminder_lead_days, share_verification_status_publicly")
      .eq("user_id", userId)
      .maybeSingle();

    return {
      docs: docs ?? [],
      profile: prof ?? null,
      prefs: prefs ?? null,
      requiredTypes: REQUIRED_KYC_DOC_TYPES as ReadonlyArray<string>,
      expiryRequiredTypes: EXPIRY_REQUIRED_DOC_TYPES as ReadonlyArray<string>,
    };
  });

// ----- Save reminder lead-time preferences -----
export const updateKycReminderPrefs = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      leadDays: z.array(z.number().int().min(1).max(180)).min(1).max(6),
      sharePublicly: z.boolean(),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const sorted = [...new Set(data.leadDays)].sort((a, b) => b - a);
    const { error } = await supabase.from("notification_preferences").upsert({
      user_id: userId,
      kyc_reminder_lead_days: sorted,
      share_verification_status_publicly: data.sharePublicly,
    }, { onConflict: "user_id" });
    if (error) throw new Error(error.message);
    return { ok: true, leadDays: sorted };
  });

// ----- Submit KYC for admin review -----
export const submitKycForReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: docs } = await supabase
      .from("helper_documents")
      .select("id, doc_type, scan_status")
      .eq("helper_id", userId)
      .in("status", ["submitted", "approved"]);
    const valid = (docs ?? []).filter((d) => d.scan_status !== "quarantined");
    const have = new Set(valid.map((d) => d.doc_type));
    const missing = REQUIRED_KYC_DOC_TYPES.filter((t) => !have.has(t));
    if (missing.length) throw new Error(`Please upload: ${missing.join(", ")}`);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Determine attempt number — bump if the previous attempt was rejected/more_info_required.
    const { data: prof } = await supabaseAdmin
      .from("helper_profiles")
      .select("verification_status, verification_attempt_number")
      .eq("id", userId)
      .maybeSingle();
    const fromStatus = (prof as { verification_status?: string } | null)?.verification_status ?? "draft";
    const currentAttempt = (prof as { verification_attempt_number?: number } | null)?.verification_attempt_number ?? 1;
    const nextAttempt = ["rejected", "more_info_required"].includes(fromStatus)
      ? currentAttempt + 1
      : currentAttempt;

    await supabaseAdmin.from("helper_profiles").upsert({
      id: userId,
      kyc_submitted_at: new Date().toISOString(),
      verification_status: "pending",
      verification_attempt_number: nextAttempt,
    } as never, { onConflict: "id" });

    // Re-tag any documents still on the old attempt number to this attempt.
    if (nextAttempt !== currentAttempt) {
      await supabaseAdmin.from("helper_documents")
        .update({ attempt_number: nextAttempt } as never)
        .eq("helper_id", userId)
        .eq("status", "submitted");
    }

    // Create the verification attempt row + audit event.
    const docIds = valid.map((d) => (d as { id: string }).id);
    const { data: attempt } = await supabaseAdmin
      .from("helper_verification_attempts")
      .insert({
        helper_id: userId,
        attempt_number: nextAttempt,
        status: "submitted",
        document_ids: docIds,
      } as never)
      .select("id")
      .single();

    await supabaseAdmin.from("helper_verification_audit").insert({
      helper_id: userId,
      attempt_id: (attempt as { id: string } | null)?.id ?? null,
      attempt_number: nextAttempt,
      event_type: "submitted",
      actor_id: userId,
      actor_role: "helper",
      from_status: fromStatus,
      to_status: "pending",
      notes: "Helper submitted KYC for review",
      metadata: { document_ids: docIds },
    } as never);

    await supabase.from("notifications").insert({
      user_id: userId,
      type: "kyc_submitted",
      title: "We've received your verification",
      body: "Our team typically reviews documents within 24 hours. We'll email you as soon as it's done.",
      link: "/helper/verification",
    });

    try {
      const { sendEmail } = await import("./email.server");
      const { sendUserSMS } = await import("./sms.server");
      const { data: au } = await supabaseAdmin.auth.admin.getUserById(userId);
      const email = au?.user?.email;
      if (email) await sendEmail({ to: email, userId, template: "kyc_submitted_helper", idempotencyKey: `kyc_submitted:${userId}:${nextAttempt}` });
      await sendUserSMS(userId, "Peopels: Verification received. We typically review within 24 hours.");
    } catch (err) { console.error("[kyc.submit] notify failed", err); }

    return { ok: true, attempt_number: nextAttempt };
  });

// ----- Signed URL: helper can read their own docs; admin can read any -----
export const getDocSignedUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ docId: z.string().uuid(), download: z.boolean().optional() }).parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: doc, error } = await supabase
      .from("helper_documents").select("storage_path, helper_id, original_filename, doc_type").eq("id", data.docId).single();
    if (error || !doc) throw new Error("doc_not_found");

    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (doc.helper_id !== userId && !isAdmin) throw new Error("forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const opts = data.download
      ? { download: doc.original_filename ?? `${doc.doc_type}.bin` }
      : undefined;
    const { data: signed, error: sErr } = await supabaseAdmin.storage
      .from("helper-kyc")
      .createSignedUrl(doc.storage_path, 60 * 10, opts);
    if (sErr || !signed) throw new Error("could_not_sign");
    return { url: signed.signedUrl };
  });

// ----- Public: list active decision codes for the admin UI -----
export const listKycDecisionCodes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const { data } = await context.supabase
      .from("kyc_decision_codes")
      .select("code, decision, label, description, sort_order")
      .eq("active", true)
      .order("sort_order");
    return { codes: data ?? [] };
  });

// ----- Admin: pending KYC queue -----
export const adminListKycQueue = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: helpersRaw, error } = await supabaseAdmin
      .from("helper_profiles")
      .select(`id, verification_status, kyc_submitted_at, kyc_reviewed_at, kyc_reviewer_notes,
        profile:profiles!helper_profiles_id_fkey(id, full_name)`)
      .in("verification_status", ["pending", "rejected", "more_info_required"])
      .order("kyc_submitted_at", { ascending: true, nullsFirst: false })
      .limit(100);
    if (error) throw new Error(error.message);
    const helpers = (helpersRaw ?? []) as Array<{
      id: string; verification_status: string;
      kyc_submitted_at: string | null; kyc_reviewed_at: string | null; kyc_reviewer_notes: string | null;
      profile: { id: string; full_name: string | null } | null;
    }>;

    const ids = helpers.map((h) => h.id);
    const { data: docsRaw } = ids.length
      ? await supabaseAdmin.from("helper_documents")
          .select("id, helper_id, doc_type, status, original_filename, created_at, reviewer_notes, expires_at, issued_at, reference_number, scan_status, decision_code")
          .in("helper_id", ids)
      : { data: [] as Array<{ id: string; helper_id: string; doc_type: string; status: string; original_filename: string | null; created_at: string; reviewer_notes: string | null; expires_at: string | null; issued_at: string | null; reference_number: string | null; scan_status: string; decision_code: string | null }> };
    return { helpers, docs: docsRaw ?? [] };
  });

// ----- Admin: approve or reject a Helper's KYC -----
export const adminDecideKyc = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      helperId: z.string().uuid(),
      decision: z.enum(["approved", "rejected", "more_info_required"]),
      decisionCode: z.string().trim().min(2).max(64),
      notes: z.string().trim().max(2000).optional(),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { recordAudit } = await import("./audit.server");

    // Validate code matches decision
    const { data: code } = await supabaseAdmin.from("kyc_decision_codes")
      .select("code, decision, label").eq("code", data.decisionCode).eq("active", true).maybeSingle();
    if (!code || code.decision !== data.decision) throw new Error("invalid_decision_code");

    const verification =
      data.decision === "approved" ? "verified"
        : data.decision === "more_info_required" ? "more_info_required"
        : "rejected";
    const before = await supabaseAdmin.from("helper_profiles").select("verification_status, kyc_reviewer_notes, verification_attempt_number").eq("id", data.helperId).maybeSingle();
    const fromStatus = (before.data as { verification_status?: string } | null)?.verification_status ?? null;
    const attemptNumber = (before.data as { verification_attempt_number?: number } | null)?.verification_attempt_number ?? 1;

    await supabaseAdmin.from("helper_profiles").update({
      verification_status: verification,
      id_verified: data.decision === "approved",
      kyc_reviewed_at: new Date().toISOString(),
      kyc_reviewer_notes: data.notes ?? null,
      more_info_requested_at: data.decision === "more_info_required" ? new Date().toISOString() : null,
      more_info_request_notes: data.decision === "more_info_required" ? (data.notes ?? code.label) : null,
    } as never).eq("id", data.helperId);

    if (data.decision !== "more_info_required") {
      await supabaseAdmin.from("helper_documents").update({
        status: data.decision,
        reviewer_id: context.userId,
        reviewer_notes: data.notes ?? null,
        reviewed_at: new Date().toISOString(),
        decision_code: data.decisionCode,
      } as never).eq("helper_id", data.helperId).eq("status", "submitted");
    }

    // Update the latest verification attempt + audit event
    const { data: latestAttempt } = await supabaseAdmin
      .from("helper_verification_attempts")
      .select("id")
      .eq("helper_id", data.helperId)
      .eq("attempt_number", attemptNumber)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    const attemptId = (latestAttempt as { id: string } | null)?.id ?? null;

    if (attemptId) {
      await supabaseAdmin.from("helper_verification_attempts").update({
        status: data.decision,
        decided_at: new Date().toISOString(),
        decision: data.decision,
        decision_code: data.decisionCode,
        reviewer_id: context.userId,
        reviewer_notes: data.notes ?? null,
      } as never).eq("id", attemptId);
    }

    await supabaseAdmin.from("helper_verification_audit").insert({
      helper_id: data.helperId,
      attempt_id: attemptId,
      attempt_number: attemptNumber,
      event_type: data.decision,
      actor_id: context.userId,
      actor_role: "admin",
      from_status: fromStatus,
      to_status: verification,
      decision_code: data.decisionCode,
      notes: data.notes ?? code.label,
      metadata: { decision_label: code.label },
    } as never);

    // Per-event notification preferences (default on).
    const { data: prefsRow } = await supabaseAdmin
      .from("notification_preferences")
      .select("kyc_inapp_approved, kyc_inapp_rejected, kyc_inapp_more_info, kyc_email_approved, kyc_email_rejected, kyc_email_more_info")
      .eq("user_id", data.helperId)
      .maybeSingle();
    const prefs = (prefsRow ?? {}) as Record<string, boolean | null>;
    const inappKey =
      data.decision === "approved" ? "kyc_inapp_approved"
        : data.decision === "more_info_required" ? "kyc_inapp_more_info"
        : "kyc_inapp_rejected";
    const emailKey =
      data.decision === "approved" ? "kyc_email_approved"
        : data.decision === "more_info_required" ? "kyc_email_more_info"
        : "kyc_email_rejected";
    const wantInapp = prefs[inappKey] !== false;
    const wantEmail = prefs[emailKey] !== false;

    // Deep-link helper to the specific attempt on their verification page.
    const helperLink = `/helper/verification?attempt=${attemptNumber}`;

    if (wantInapp) {
      await supabaseAdmin.from("notifications").insert({
        user_id: data.helperId,
        type: "kyc_decision",
        title:
          data.decision === "approved" ? "Your account is verified"
            : data.decision === "more_info_required" ? "We need more information to verify your account"
            : "Verification rejected",
        body: (data.notes ? data.notes + " — " : "") + (code.label),
        link: helperLink,
        data: {
          decision: data.decision,
          decision_code: data.decisionCode,
          attempt_id: attemptId,
          attempt_number: attemptNumber,
        },
      });
    }

    try {
      const { sendUserSMS } = await import("./sms.server");
      if (wantEmail) {
        const { data: au } = await supabaseAdmin.auth.admin.getUserById(data.helperId);
        const email = au?.user?.email;
        const template =
          data.decision === "approved" ? "kyc_approved_helper"
            : data.decision === "more_info_required" ? "kyc_more_info_helper"
            : "kyc_rejected_helper";
        if (email) {
          await sendKycEmailWithTracking({
            helperId: data.helperId,
            attemptNumber,
            decision: data.decision,
            template,
            email,
            payload: { notes: data.notes ?? "", reason: code.label, code: data.decisionCode },
          });
        }
      }
      await sendUserSMS(
        data.helperId,
        data.decision === "approved"
          ? "Peopels: You're verified ✓ — your profile is now live."
          : data.decision === "more_info_required"
            ? `Peopels: More info needed for verification — ${code.label}. ${data.notes ?? ""}`.trim()
            : `Peopels: Verification rejected — ${code.label}. ${data.notes ?? ""}`.trim(),
      );
    } catch (err) { console.error("[kyc.decide] notify failed", err); }

    await recordAudit({
      actorId: context.userId,
      action: `kyc.${data.decision}`,
      entityType: "helper_profile",
      entityId: data.helperId,
      before: before.data ?? null,
      after: { verification_status: verification, decision_code: data.decisionCode, decision_label: code.label, notes: data.notes ?? null, attempt_number: attemptNumber },
      notes: `${data.decisionCode} — ${code.label}`,
    });

    return { ok: true, attempt_number: attemptNumber };
  });

// ----- Admin: full verification history (attempts + audit + all docs incl. superseded) -----
export const adminGetVerificationHistory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      helperId: z.string().uuid(),
      // Audit pagination + filters
      auditLimit: z.number().int().min(1).max(200).default(50),
      auditOffset: z.number().int().min(0).default(0),
      auditSort: z.enum(["asc", "desc"]).default("desc"),
      filterEventType: z.string().optional(),
      filterStatus: z.string().optional(),
      filterDocType: z.string().optional(),
      filterScanVerdict: z.string().optional(),
      search: z.string().optional(),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let auditQuery = supabaseAdmin.from("helper_verification_audit")
      .select("id, attempt_id, attempt_number, event_type, actor_id, actor_role, from_status, to_status, decision_code, notes, metadata, created_at", { count: "exact" })
      .eq("helper_id", data.helperId);

    if (data.filterEventType && data.filterEventType !== "all") {
      auditQuery = auditQuery.eq("event_type", data.filterEventType);
    }
    if (data.filterStatus && data.filterStatus !== "all") {
      auditQuery = auditQuery.or(`from_status.eq.${data.filterStatus},to_status.eq.${data.filterStatus}`);
    }
    if (data.filterDocType && data.filterDocType !== "all") {
      auditQuery = auditQuery.eq("metadata->>doc_type", data.filterDocType);
    }
    if (data.filterScanVerdict && data.filterScanVerdict !== "all") {
      auditQuery = auditQuery.eq("metadata->scan->>verdict", data.filterScanVerdict);
    }
    if (data.search && data.search.trim()) {
      const s = data.search.trim().replace(/[%,]/g, " ");
      auditQuery = auditQuery.or(
        `notes.ilike.%${s}%,event_type.ilike.%${s}%,decision_code.ilike.%${s}%,from_status.ilike.%${s}%,to_status.ilike.%${s}%`,
      );
    }

    auditQuery = auditQuery
      .order("created_at", { ascending: data.auditSort === "asc" })
      .range(data.auditOffset, data.auditOffset + data.auditLimit - 1);

    const [attempts, audit, docs, distincts] = await Promise.all([
      supabaseAdmin.from("helper_verification_attempts")
        .select("id, attempt_number, status, submitted_at, decided_at, decision, decision_code, reviewer_id, reviewer_notes, document_ids, created_at")
        .eq("helper_id", data.helperId)
        .order("attempt_number", { ascending: false }),
      auditQuery,
      supabaseAdmin.from("helper_documents")
        .select("id, doc_type, status, attempt_number, original_filename, mime_type, size_bytes, scan_status, decision_code, reviewer_notes, reviewed_at, superseded_at, superseded_by, created_at")
        .eq("helper_id", data.helperId)
        .order("created_at", { ascending: false }),
      // Distinct facets for filter dropdowns (small unfiltered sample)
      supabaseAdmin.from("helper_verification_audit")
        .select("event_type, from_status, to_status, metadata")
        .eq("helper_id", data.helperId)
        .limit(1000),
    ]);

    const eventTypes = new Set<string>();
    const statusTypes = new Set<string>();
    const docTypeSet = new Set<string>();
    for (const r of (distincts.data ?? []) as Array<{ event_type: string; from_status: string | null; to_status: string | null; metadata: Record<string, unknown> | null }>) {
      eventTypes.add(r.event_type);
      if (r.from_status) statusTypes.add(r.from_status);
      if (r.to_status) statusTypes.add(r.to_status);
      const dt = (r.metadata as { doc_type?: string } | null)?.doc_type;
      if (dt) docTypeSet.add(dt);
    }

    return {
      attempts: attempts.data ?? [],
      audit: audit.data ?? [],
      auditTotal: audit.count ?? 0,
      documents: docs.data ?? [],
      facets: {
        eventTypes: Array.from(eventTypes).sort(),
        statusTypes: Array.from(statusTypes).sort(),
        docTypes: Array.from(docTypeSet).sort(),
      },
    };
  });

// ----- KYC email send-with-tracking + retry log -----
type SendKycEmailInput = {
  helperId: string;
  attemptNumber: number;
  decision: "approved" | "rejected" | "more_info_required";
  template: "kyc_approved_helper" | "kyc_rejected_helper" | "kyc_more_info_helper";
  email: string;
  payload: Record<string, unknown>;
  retryOf?: string; // existing kyc_email_delivery row id
};

async function sendKycEmailWithTracking(input: SendKycEmailInput): Promise<{ ok: boolean; deliveryId: string }> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { sendEmail } = await import("./email.server");

  // Reuse an existing row when retrying, else create a new one.
  let deliveryId = input.retryOf ?? "";
  let attemptCount = 0;
  let baseKey = `kyc_${input.decision}:${input.helperId}:${input.attemptNumber}`;

  if (input.retryOf) {
    const { data: existing } = await supabaseAdmin
      .from("kyc_email_delivery")
      .select("id, attempt_count, idempotency_key")
      .eq("id", input.retryOf)
      .maybeSingle();
    if (existing) {
      attemptCount = (existing as { attempt_count?: number }).attempt_count ?? 0;
      baseKey = String((existing as { idempotency_key?: string }).idempotency_key ?? baseKey).replace(/:r\d+$/, "");
    }
  } else {
    const { data: inserted, error } = await supabaseAdmin
      .from("kyc_email_delivery")
      .insert({
        helper_id: input.helperId,
        attempt_number: input.attemptNumber,
        decision: input.decision,
        template: input.template,
        recipient_email: input.email,
        idempotency_key: baseKey,
        status: "pending",
        attempt_count: 0,
        metadata: input.payload as never,
      })
      .select("id")
      .single();
    if (error) {
      // 23505 = unique violation; already exists — load it.
      const { data: dup } = await supabaseAdmin
        .from("kyc_email_delivery")
        .select("id, attempt_count")
        .eq("idempotency_key", baseKey)
        .maybeSingle();
      if (!dup) {
        console.error("[kyc-email] insert failed", error);
        return { ok: false, deliveryId: "" };
      }
      deliveryId = dup.id;
      attemptCount = (dup as { attempt_count?: number }).attempt_count ?? 0;
    } else {
      deliveryId = inserted!.id;
    }
  }

  // Pull current row to honour next_retry_at + circuit breaker.
  const { data: currentRow } = await supabaseAdmin
    .from("kyc_email_delivery")
    .select("max_attempts, next_retry_at, status, metadata")
    .eq("id", deliveryId)
    .maybeSingle();
  const maxAttempts = (currentRow as { max_attempts?: number } | null)?.max_attempts ?? 5;
  const existingMeta = ((currentRow as { metadata?: Record<string, unknown> } | null)?.metadata ?? {}) as Record<string, unknown>;
  const nextRetryAt = (currentRow as { next_retry_at?: string | null } | null)?.next_retry_at ?? null;
  if (nextRetryAt && new Date(nextRetryAt).getTime() > Date.now() && attemptCount > 0) {
    return { ok: false, deliveryId };
  }
  if (attemptCount >= maxAttempts) {
    // Circuit open — don't attempt another send.
    await supabaseAdmin.from("kyc_email_delivery").update({
      status: "failed",
      metadata: { ...existingMeta, circuit_open: true, circuit_open_at: new Date().toISOString() } as never,
    } as never).eq("id", deliveryId);
    return { ok: false, deliveryId };
  }

  const nextAttempt = attemptCount + 1;
  const idemKey = nextAttempt === 1 ? baseKey : `${baseKey}:r${nextAttempt - 1}`;

  let okSend = true;
  let errMsg: string | null = null;
  try {
    await sendEmail({
      to: input.email,
      userId: input.helperId,
      template: input.template,
      idempotencyKey: idemKey,
      data: input.payload as never,
    });
    const { data: logRow } = await supabaseAdmin
      .from("email_log")
      .select("status, error")
      .eq("idempotency_key", idemKey)
      .maybeSingle();
    if (logRow && (logRow as { status?: string }).status === "failed") {
      okSend = false;
      errMsg = (logRow as { error?: string }).error ?? "send failed";
    }
  } catch (err) {
    okSend = false;
    errMsg = err instanceof Error ? err.message : String(err);
  }

  const now = new Date().toISOString();
  // Exponential backoff with jitter: base 60s, doubling, capped at 60min.
  // Adds 0–25% jitter so concurrent retries don't pile up at the same instant.
  const backoffMs = Math.min(60_000 * 2 ** (nextAttempt - 1), 60 * 60_000);
  const jitterMs = Math.floor(backoffMs * 0.25 * Math.random());
  const circuitOpen = !okSend && nextAttempt >= maxAttempts;
  const finalStatus: "sent" | "failed" | "pending" = okSend
    ? "sent"
    : circuitOpen ? "failed" : "pending";

  await supabaseAdmin
    .from("kyc_email_delivery")
    .update({
      attempt_count: nextAttempt,
      status: finalStatus,
      last_error: okSend ? null : errMsg,
      last_attempt_at: now,
      next_retry_at: okSend || circuitOpen ? null : new Date(Date.now() + backoffMs + jitterMs).toISOString(),
      sent_at: okSend ? now : null,
      metadata: {
        ...existingMeta,
        last_backoff_ms: okSend ? null : backoffMs + jitterMs,
        circuit_open: circuitOpen ? true : false,
        circuit_open_at: circuitOpen ? now : (existingMeta as { circuit_open_at?: string }).circuit_open_at ?? null,
      } as never,
    } as never)
    .eq("id", deliveryId);

  // Raise an admin-visible alert the first time the circuit trips for this row.
  if (circuitOpen && !(existingMeta as { circuit_open?: boolean }).circuit_open) {
    try {
      const nextRetryWindow = new Date(Date.now() + backoffMs + jitterMs).toISOString();
      await supabaseAdmin.from("security_alerts").insert({
        category: "kyc_email_circuit_open",
        severity: "high",
        title: `KYC email delivery circuit opened (${input.decision.replaceAll("_", " ")})`,
        detail: `Delivery to ${input.email} failed after ${nextAttempt} attempts. Reason: ${errMsg ?? "unknown"}. Use Force retry to override.`,
        metric: nextAttempt,
        threshold: maxAttempts,
        window_start: now,
        window_end: nextRetryWindow,
        payload: {
          delivery_id: deliveryId,
          helper_id: input.helperId,
          recipient: input.email,
          decision: input.decision,
          template: input.template,
          reason: errMsg,
          attempt_count: nextAttempt,
          max_attempts: maxAttempts,
          next_retry_window: nextRetryWindow,
        } as never,
      } as never);
    } catch { /* never block on alert */ }
  }

  return { ok: okSend, deliveryId };
}


// ----- Admin: list KYC email failures + retry -----
const KycEmailListInput = z.object({
  status: z.enum(["all", "pending", "sent", "failed"]).default("all"),
  q: z.string().trim().max(200).optional(),
  sort: z.enum(["created_at", "last_attempt_at", "attempt_count", "next_retry_at"]).default("created_at"),
  order: z.enum(["asc", "desc"]).default("desc"),
  circuitOnly: z.boolean().optional(),
  limit: z.number().int().min(1).max(200).default(50),
  offset: z.number().int().min(0).default(0),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyKycEmailFilters(q: any, data: z.infer<typeof KycEmailListInput>): any {
  let out = q;
  if (data.status !== "all") out = out.eq("status", data.status);
  if (data.circuitOnly) out = out.contains("metadata", { circuit_open: true });
  if (data.q && data.q.length) {
    const term = data.q.replace(/[%,]/g, " ").trim();
    if (term) {
      out = out.or(
        `recipient_email.ilike.%${term}%,last_error.ilike.%${term}%,decision.ilike.%${term}%,template.ilike.%${term}%`,
      );
    }
  }
  out = out.order(data.sort, { ascending: data.order === "asc" });
  return out;
}

export const adminListKycEmailDeliveries = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => KycEmailListInput.parse(i ?? {}))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const base = supabaseAdmin.from("kyc_email_delivery")
      .select("id, helper_id, attempt_number, decision, template, recipient_email, status, attempt_count, max_attempts, last_error, last_attempt_at, next_retry_at, sent_at, created_at, metadata", { count: "exact" });
    const filtered = applyKycEmailFilters(base, data);
    const { data: rows, count } = await filtered.range(data.offset, data.offset + data.limit - 1);
    return { items: rows ?? [], total: count ?? 0 };
  });

// CSV export — uses the exact same filters, search, and sort as the list view.
export const adminExportKycEmailDeliveriesCsv = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => KycEmailListInput.extend({ max: z.number().int().min(1).max(5000).default(5000) }).parse(i ?? {}))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const base = supabaseAdmin.from("kyc_email_delivery")
      .select("id, helper_id, attempt_number, decision, template, recipient_email, status, attempt_count, max_attempts, last_error, last_attempt_at, next_retry_at, sent_at, created_at, metadata");
    const filtered = applyKycEmailFilters(base, data);
    const { data: rows } = await filtered.limit(data.max);
    const headers = [
      "id","helper_id","decision","template","recipient_email","status","attempt_count","max_attempts",
      "circuit_open","last_error","last_attempt_at","next_retry_at","sent_at","created_at",
    ];
    const esc = (v: unknown): string => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/\r?\n/g, " ").replace(/"/g, '""');
      return /[",]/.test(s) ? `"${s}"` : s;
    };
    const lines = [
      `# Peopels KYC email deliveries export — generated ${new Date().toISOString()}`,
      `# filters status=${data.status} q=${data.q ?? ""} sort=${data.sort} order=${data.order} circuitOnly=${data.circuitOnly ? "1" : "0"} rows=${(rows ?? []).length}`,
      headers.join(","),
      ...((rows ?? []) as Array<Record<string, unknown>>).map((r) => [
        r.id, r.helper_id, r.decision, r.template, r.recipient_email, r.status, r.attempt_count, r.max_attempts,
        (r.metadata as { circuit_open?: boolean } | null)?.circuit_open ? "yes" : "no",
        r.last_error, r.last_attempt_at, r.next_retry_at, r.sent_at, r.created_at,
      ].map(esc).join(",")),
    ];
    return {
      csv: lines.join("\n"),
      count: (rows ?? []).length,
      filename: `kyc_email_deliveries_${data.status}_${new Date().toISOString().slice(0, 10)}.csv`,
    };
  });


export const adminRetryKycEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({
    deliveryId: z.string().uuid(),
    force: z.boolean().optional(),
  }).parse(i))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("kyc_email_delivery")
      .select("id, helper_id, attempt_number, decision, template, recipient_email, attempt_count, max_attempts, next_retry_at, metadata")
      .eq("id", data.deliveryId)
      .maybeSingle();
    if (!row) throw new Error("delivery row not found");
    const r = row as {
      id: string; helper_id: string; attempt_number: number; decision: string;
      template: string; recipient_email: string; attempt_count: number; max_attempts: number;
      next_retry_at: string | null;
      metadata: Record<string, unknown> | null;
    };

    if (data.force) {
      // Admin override: reset the circuit so we can attempt one more send.
      await supabaseAdmin.from("kyc_email_delivery").update({
        attempt_count: Math.max(0, (r.max_attempts ?? 5) - 1),
        status: "pending",
        next_retry_at: null,
        metadata: { ...(r.metadata ?? {}), circuit_open: false, force_reset_at: new Date().toISOString() } as never,
      } as never).eq("id", r.id);
    } else {
      if (r.attempt_count >= r.max_attempts) throw new Error("circuit_open: max attempts reached — use Force retry to override");
      if (r.next_retry_at && new Date(r.next_retry_at).getTime() > Date.now()) {
        const secs = Math.ceil((new Date(r.next_retry_at).getTime() - Date.now()) / 1000);
        throw new Error(`backoff: next retry available in ${secs}s`);
      }
    }

    const result = await sendKycEmailWithTracking({
      helperId: r.helper_id,
      attemptNumber: r.attempt_number,
      decision: r.decision as "approved" | "rejected" | "more_info_required",
      template: r.template as "kyc_approved_helper" | "kyc_rejected_helper" | "kyc_more_info_helper",
      email: r.recipient_email,
      payload: r.metadata ?? {},
      retryOf: r.id,
    });
    return { ok: result.ok };
  });

// ----- Admin: KYC email deliverability stats (charts) -----
export const adminKycEmailStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ days: z.number().int().min(1).max(90).default(30) }).parse(i ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const since = new Date(Date.now() - data.days * 86_400_000).toISOString();

    const { data: rows } = await supabaseAdmin
      .from("kyc_email_delivery")
      .select("status, created_at, sent_at, attempt_count, decision, recipient_email, last_error")
      .gte("created_at", since)
      .order("created_at", { ascending: true });
    const all = (rows ?? []) as Array<{ status: string; created_at: string; sent_at: string | null; attempt_count: number; decision: string; recipient_email: string; last_error: string | null }>;

    // Look up bounce/complaint suppressions for the recipients in window.
    const recipients = Array.from(new Set(all.map((r) => r.recipient_email).filter(Boolean)));
    const suppMap = new Map<string, { reason: string; last_event_at: string }>();
    if (recipients.length) {
      const { data: supp } = await supabaseAdmin
        .from("suppressed_emails")
        .select("email, reason, last_event_at")
        .in("email", recipients);
      for (const s of (supp ?? []) as Array<{ email: string; reason: string; last_event_at: string }>) {
        suppMap.set(String(s.email).toLowerCase(), { reason: s.reason, last_event_at: s.last_event_at });
      }
    }
    const isBounceErr = (e: string | null) => !!e && /bounc|undeliver|550|invalid recipient|mailbox/i.test(e);
    const isComplaintErr = (e: string | null) => !!e && /complain|spam|abuse/i.test(e);

    const byDay = new Map<string, { day: string; sent: number; failed: number; pending: number; total: number; bounces: number; complaints: number; avg_attempts: number }>();
    const dayAttemptSum = new Map<string, { sum: number; n: number }>();
    for (let i = data.days - 1; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86_400_000).toISOString().slice(0, 10);
      byDay.set(d, { day: d, sent: 0, failed: 0, pending: 0, total: 0, bounces: 0, complaints: 0, avg_attempts: 0 });
      dayAttemptSum.set(d, { sum: 0, n: 0 });
    }
    let bounceCount = 0;
    let complaintCount = 0;
    for (const r of all) {
      const d = r.created_at.slice(0, 10);
      const bucket = byDay.get(d);
      const accum = dayAttemptSum.get(d);
      if (!bucket || !accum) continue;
      bucket.total += 1;
      accum.sum += r.attempt_count ?? 0;
      accum.n += 1;
      if (r.status === "sent") bucket.sent += 1;
      else if (r.status === "failed") bucket.failed += 1;
      else bucket.pending += 1;

      const supp = suppMap.get(String(r.recipient_email).toLowerCase());
      const isBounce = (supp && supp.reason === "bounce") || isBounceErr(r.last_error);
      const isComplaint = (supp && supp.reason === "complaint") || isComplaintErr(r.last_error);
      if (isBounce) { bucket.bounces += 1; bounceCount += 1; }
      if (isComplaint) { bucket.complaints += 1; complaintCount += 1; }
    }
    for (const [d, a] of dayAttemptSum) {
      const b = byDay.get(d)!;
      b.avg_attempts = a.n ? Math.round((a.sum / a.n) * 100) / 100 : 0;
    }

    const totals = {
      total: all.length,
      sent: all.filter((r) => r.status === "sent").length,
      failed: all.filter((r) => r.status === "failed").length,
      pending: all.filter((r) => r.status === "pending").length,
    };
    const retriesNeeded = all.filter((r) => r.attempt_count > 1).length;
    const deliverabilityRate = totals.total > 0
      ? Math.round((totals.sent / totals.total) * 1000) / 10
      : 100;
    const bounceRate = totals.total > 0 ? Math.round((bounceCount / totals.total) * 1000) / 10 : 0;
    const complaintRate = totals.total > 0 ? Math.round((complaintCount / totals.total) * 1000) / 10 : 0;
    const totalAttempts = all.reduce((s, r) => s + (r.attempt_count ?? 0), 0);
    const avgRetryCount = all.length > 0 ? Math.round((totalAttempts / all.length) * 100) / 100 : 0;

    const byDecision: Record<string, { sent: number; failed: number; pending: number }> = {};
    for (const r of all) {
      const k = r.decision || "unknown";
      const b = byDecision[k] ?? { sent: 0, failed: 0, pending: 0 };
      if (r.status === "sent") b.sent += 1;
      else if (r.status === "failed") b.failed += 1;
      else b.pending += 1;
      byDecision[k] = b;
    }

    // Active circuit-breaker alerts that haven't been acknowledged yet.
    const { data: openAlerts } = await supabaseAdmin
      .from("security_alerts")
      .select("id, detected_at, title, detail, payload, severity")
      .eq("category", "kyc_email_circuit_open")
      .is("acknowledged_at", null)
      .order("detected_at", { ascending: false })
      .limit(20);

    return {
      days: data.days,
      series: Array.from(byDay.values()),
      totals: {
        ...totals, retriesNeeded, deliverabilityRate,
        bounceCount, complaintCount, bounceRate, complaintRate, avgRetryCount,
      },
      byDecision,
      circuitAlerts: openAlerts ?? [],
    };
  });

// Admin: list open circuit-breaker alerts and acknowledge one.
export const adminListCircuitAlerts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({
    onlyOpen: z.boolean().default(true),
    limit: z.number().int().min(1).max(100).default(20),
  }).parse(i ?? {}))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin.from("security_alerts")
      .select("id, detected_at, title, detail, payload, severity, acknowledged_at, acknowledged_by, window_end")
      .eq("category", "kyc_email_circuit_open")
      .order("detected_at", { ascending: false })
      .limit(data.limit);
    if (data.onlyOpen) q = q.is("acknowledged_at", null);
    const { data: rows } = await q;
    return { items: rows ?? [] };
  });

export const adminAckCircuitAlert = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("security_alerts")
      .update({ acknowledged_at: new Date().toISOString(), acknowledged_by: context.userId } as never)
      .eq("id", data.id);
    return { ok: true };
  });



// ----- Helper: my own verification history -----
export const getMyVerificationHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const [attempts, audit] = await Promise.all([
      supabase.from("helper_verification_attempts")
        .select("id, attempt_number, status, submitted_at, decided_at, decision, decision_code, reviewer_notes, created_at")
        .eq("helper_id", userId)
        .order("attempt_number", { ascending: false }),
      supabase.from("helper_verification_audit")
        .select("id, attempt_number, event_type, actor_role, from_status, to_status, decision_code, notes, created_at")
        .eq("helper_id", userId)
        .order("created_at", { ascending: false })
        .limit(100),
    ]);
    return { attempts: attempts.data ?? [], audit: audit.data ?? [] };
  });

// ----- Per-event KYC notification preferences (in-app + email) -----
export const getKycEventPrefs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("notification_preferences")
      .select("kyc_inapp_approved, kyc_inapp_rejected, kyc_inapp_more_info, kyc_email_approved, kyc_email_rejected, kyc_email_more_info")
      .eq("user_id", userId)
      .maybeSingle();
    return {
      prefs: data ?? {
        kyc_inapp_approved: true, kyc_inapp_rejected: true, kyc_inapp_more_info: true,
        kyc_email_approved: true, kyc_email_rejected: true, kyc_email_more_info: true,
      },
    };
  });

export const updateKycEventPrefs = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      kyc_inapp_approved: z.boolean(),
      kyc_inapp_rejected: z.boolean(),
      kyc_inapp_more_info: z.boolean(),
      kyc_email_approved: z.boolean(),
      kyc_email_rejected: z.boolean(),
      kyc_email_more_info: z.boolean(),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("notification_preferences")
      .upsert({ user_id: userId, ...data } as never, { onConflict: "user_id" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });
